// src/layouts/crm/clients/index.js
import { useEffect, useState, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import MDAvatar from "components/MDAvatar";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";

import { useNavigate } from "react-router-dom";

import { supabase } from "../../../lib/supa";

function Clients() {
  const columns = useMemo(
    () => [
      { Header: "Ενέργειες", accessor: "open", width: "10%", align: "center" },
      { Header: "ΟΝΟΜΑ", accessor: "first_name", width: "15%" },
      { Header: "ΕΠΙΘΕΤΟ", accessor: "last_name", width: "15%" },
      { Header: "ΕΤΑΙΡΕΙΑ", accessor: "company" },
      { Header: "EMAIL", accessor: "email" },
      { Header: "ΚΙΝΗΤΟ", accessor: "mobile", width: "15%" },
      { Header: "ΚΑΤΑΣΤΑΣΗ", accessor: "status", width: "10%" },
    ],
    []
  );

  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [sort, setSort] = useState({ field: "last_name", direction: "asc" });

  // navigation
  const navigate = useNavigate();

  // Dialog + form state
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [editId, setEditId] = useState(null); // null = νέα εγγραφή, uuid = edit
  // νέο state για επιβεβαίωση διαγραφής
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  const emptyForm = {
    salutation: "", // Κύριος/Κυρία ή κενό
    first_name: "",
    last_name: "",
    company: "",
    email: "",
    mobile: "",
    phone: "",
    status: "active",
  };

  const [form, setForm] = useState(emptyForm);

  const renderStatus = (status) => {
    const s = String(status || "").toLowerCase();
    const map = {
      active: { label: "Ενεργός", color: "success" },
      inactive: { label: "Ανενεργός", color: "secondary" },
    };
    const { label, color } = map[s] || { label: String(status || ""), color: "info" };
    return (
      <MDBox ml={-1}>
        <MDBadge badgeContent={label} color={color} variant="gradient" size="sm" />
      </MDBox>
    );
  };

  // === Create row helper (κρατάμε ενιαία δομή) ===
  const makeRow = (r) => ({
    first_name: r.first_name || "-",
    last_name: r.last_name || "-",
    company: r.company || "-",
    email: r.email || "-",
    mobile: r.mobile || "-",
    status: renderStatus(r.status || "active"),
    _id: r.id,
    open: (
      <MDBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
        {/* Προβολή Πελάτη */}
        <Tooltip title="Προβολή" arrow>
          <IconButton
            size="medium"
            onClick={() => navigate(`/services?client=${r.id}`)}
            sx={{ color: "info.main", "&:hover": { color: "info.dark" } }}
          >
            <Icon>manage_accounts</Icon>
          </IconButton>
        </Tooltip>

        {/* Επεξεργασία Πελάτη */}
        <Tooltip title="Επεξεργασία" arrow>
          <IconButton
            size="medium"
            onClick={() => navigate(`/client-view/${r.id}`)}
            sx={{ color: "warning.main", "&:hover": { color: "warning.dark" } }}
          >
            <Icon>edit</Icon>
          </IconButton>
        </Tooltip>

        {/* Διαγραφή Πελάτη */}
        <Tooltip title="Διαγραφή" arrow>
          <IconButton
            size="medium"
            onClick={() => handleDelete(r.id)}
            sx={{ color: "error.main", "&:hover": { color: "error.dark" } }}
          >
            <Icon>delete</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  });

  // === Load ===
  useEffect(() => {
    let on = true;
    setLoading(true);

    (async () => {
      try {
        const { data, error } = await supabase
          .from("clients")
          .select(
            "id, salutation, first_name, last_name, company, email, mobile, phone, status, created_at"
          )
          .order("created_at", { ascending: false });

        if (error) throw error;

        const mapped = (data ?? []).map((r) => makeRow(r));
        if (on) setRows(mapped);
      } catch (e) {
        if (on) setError(e.message || "ΣΦΑΛΜΑ");
      } finally {
        if (on) setLoading(false);
      }
    })();

    return () => {
      on = false;
    };
  }, []);

  // === Filters & Sorting ===
  const filteredRows = rows
    .filter((row) =>
      showOnlyActive ? row.status?.props?.children?.props?.badgeContent === "Ενεργός" : true
    )
    .filter((row) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        String(row.first_name).toLowerCase().includes(q) ||
        String(row.last_name).toLowerCase().includes(q) ||
        String(row.company).toLowerCase().includes(q) ||
        String(row.email).toLowerCase().includes(q) ||
        String(row.mobile).toLowerCase().includes(q)
      );
    });

  const sortedRows = sort.field
    ? [...filteredRows].sort((a, b) => {
        const vA = String(a[sort.field] ?? "").toLowerCase();
        const vB = String(b[sort.field] ?? "").toLowerCase();
        if (vA < vB) return sort.direction === "asc" ? -1 : 1;
        if (vA > vB) return sort.direction === "asc" ? 1 : -1;
        return 0;
      })
    : filteredRows;

  const handleSort = (field) => {
    setSort((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { field, direction: "asc" };
    });
  };

  // === Sortable headers ===
  const columnsWithSort = [
    { Header: "Ενεργειες", accessor: "open", width: "10%", align: "center" },
    {
      Header: (
        <MDBox
          display="flex"
          alignItems="center"
          sx={{
            cursor: "pointer",
            color: sort.field === "first_name" ? "success.main" : "inherit",
          }}
          onClick={() => handleSort("first_name")}
        >
          ΟΝΟΜΑ
          {sort.field === "first_name" && (
            <Icon fontSize="small" sx={{ ml: 0.5 }}>
              {sort.direction === "asc" ? "arrow_upward" : "arrow_downward"}
            </Icon>
          )}
        </MDBox>
      ),
      accessor: "first_name",
      width: "15%",
    },
    {
      Header: (
        <MDBox
          display="flex"
          alignItems="center"
          sx={{ cursor: "pointer", color: sort.field === "last_name" ? "success.main" : "inherit" }}
          onClick={() => handleSort("last_name")}
        >
          ΕΠΙΘΕΤΟ
          {sort.field === "last_name" && (
            <Icon fontSize="small" sx={{ ml: 0.5 }}>
              {sort.direction === "asc" ? "arrow_upward" : "arrow_downward"}
            </Icon>
          )}
        </MDBox>
      ),
      accessor: "last_name",
      width: "15%",
    },
    { Header: "ΕΤΑΙΡΕΙΑ", accessor: "company" },
    { Header: "EMAIL", accessor: "email" },
    { Header: "ΚΙΝΗΤΟ", accessor: "mobile", width: "15%" },
    { Header: "ΚΑΤΑΣΤΑΣΗ", accessor: "status", width: "10%" },
  ];

  // === Dialog openers ===
  const handleOpenNew = () => {
    setForm({ ...emptyForm });
    setFormError(null);
    setEditId(null);
    setOpen(true);
  };

  const handleOpenEdit = async (id) => {
    try {
      setFormError(null);
      // Φέρνουμε φρέσκα δεδομένα (αν ο πίνακας έχει αλλάξει ενδιάμεσα)
      const { data, error } = await supabase
        .from("clients")
        .select("id, salutation, first_name, last_name, company, email, mobile, phone, status")
        .eq("id", id)
        .single();
      if (error) throw error;

      setForm({
        salutation: data.salutation ?? "",
        first_name: data.first_name ?? "",
        last_name: data.last_name ?? "",
        company: data.company ?? "",
        email: data.email ?? "",
        mobile: data.mobile ?? "",
        phone: data.phone ?? "",
        status: data.status ?? "active",
      });
      setEditId(data.id);
      setOpen(true);
    } catch (e) {
      setFormError(e.message || "Αποτυχία ανάκτησης πελάτη.");
      setOpen(true); // ανοίγουμε για να φανεί το σφάλμα
    }
  };

  // === Delete ===
  const handleDelete = (id) => {
    // άνοιγμα όμορφου dialog επιβεβαίωσης
    setDeleteDialog({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    const id = deleteDialog.id;
    if (!id) return;
    try {
      const { error } = await supabase.from("clients").delete().eq("id", id);
      if (error) throw error;

      setRows((prev) => prev.filter((r) => r._id !== id));
    } catch (e) {
      alert(e.message || "Αποτυχία διαγραφής.");
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  // === Validation helpers ===
  const emailOk = (x) => /\S+@\S+\.\S+/.test(String(x || "").trim());
  const requiredMissing = () =>
    !form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.mobile.trim();

  // === Submit (create/update) ===
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setFormError(null);

    if (requiredMissing()) {
      setFormError("Συμπλήρωσε Όνομα, Επίθετο, Email, Κινητό.");
      return;
    }
    if (!emailOk(form.email)) {
      setFormError("Το email δεν είναι έγκυρο.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        salutation: form.salutation?.trim() || null,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        company: form.company?.trim() || null,
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        phone: form.phone?.trim() || null,
        status: form.status || "active",
      };

      if (editId) {
        // Update
        const { data, error } = await supabase
          .from("clients")
          .update(payload)
          .eq("id", editId)
          .select(
            "id, salutation, first_name, last_name, company, email, mobile, phone, status, created_at"
          )
          .single();

        if (error) throw error;

        // ανανέωση στη λίστα
        setRows((prev) => {
          const idx = prev.findIndex((r) => r._id === editId);
          if (idx === -1) return prev;
          const updated = makeRow(data);
          const clone = [...prev];
          clone[idx] = { ...clone[idx], ...updated };
          return clone;
        });
      } else {
        // Insert
        const { data, error } = await supabase
          .from("clients")
          .insert([payload])
          .select(
            "id, salutation, first_name, last_name, company, email, mobile, phone, status, created_at"
          )
          .single();

        if (error) throw error;

        setRows((prev) => [makeRow(data), ...prev]);
      }

      // close & reset
      setOpen(false);
      setEditId(null);
      setForm({ ...emptyForm });
    } catch (err) {
      // Ευανάγνωστο μήνυμα για unique index (email ανά account)
      const msg = String(err?.message || "");
      if (msg.toLowerCase().includes("uq_clients_account_email")) {
        setFormError("Υπάρχει ήδη πελάτης με αυτό το email για τον συγκεκριμένο λογαριασμό.");
      } else {
        setFormError(msg || "Αποτυχία αποθήκευσης.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={1}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <MDTypography variant="h6" color="white">
                    ΠΕΛΑΤΕΣ
                  </MDTypography>
                  <Tooltip title="ΝΕΟΣ ΠΕΛΑΤΗΣ" arrow>
                    <MDAvatar
                      size="md"
                      bgColor="warning"
                      sx={{ cursor: "pointer" }}
                      onClick={handleOpenNew}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") handleOpenNew();
                      }}
                    >
                      <Icon sx={{ color: "white" }} fontSize="md">
                        person_add
                      </Icon>
                    </MDAvatar>
                  </Tooltip>
                </MDBox>
              </MDBox>

              {/* Search + Only active */}
              <MDBox
                px={2}
                pt={2}
                pb={1}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <MDInput
                  placeholder="Αναζήτηση πελάτη..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  sx={{ flex: 1, maxWidth: "50%" }}
                  InputProps={{
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Καθαρισμός αναζήτησης"
                          onClick={() => setSearch("")}
                          edge="end"
                        >
                          <Icon fontSize="small">close</Icon>
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <MDBox display="flex" alignItems="center" ml={2}>
                  <MDTypography variant="button" color="text" ml={1}>
                    Μόνο ενεργοί
                  </MDTypography>
                  <Switch
                    checked={showOnlyActive}
                    onChange={() => setShowOnlyActive((v) => !v)}
                    color="info"
                  />
                </MDBox>
              </MDBox>

              <MDBox pt={1}>
                {error && (
                  <MDBox px={2} pb={2} color="error.main">
                    ΣΦΑΛΜΑ: {error}
                  </MDBox>
                )}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns: columnsWithSort, rows: sortedRows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Create / Edit Client */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: "dark.main",
            color: "dark.contrastText",
            borderBottom: "1px solid",
            borderColor: "dark.main",
          }}
        >
          {editId ? "Επεξεργασία Πελάτη" : "Νέος Πελάτης"}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            bgcolor: "dark.main",
            color: "dark.contrastText",
          }}
        >
          {formError && (
            <MDBox mb={2} color="error.main">
              {formError}
            </MDBox>
          )}

          <form id="client-form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Προσφώνηση */}
              <Grid item xs={12} sm={4}>
                <MDInput
                  label="Προσφώνηση"
                  value={form.salutation}
                  onChange={(e) => setForm({ ...form, salutation: e.target.value })}
                  placeholder="π.χ. Κύριος, Κυρία, Δρ., κα."
                  fullWidth
                  inputProps={{ style: { color: "dark.contrastText" }, maxLength: 30 }}
                  InputLabelProps={{ style: { color: "#bbb" } }}
                  sx={{ "& .MuiOutlinedInput-root": { background: "dark.main" } }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <MDInput
                  label="Όνομα"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  fullWidth
                  inputProps={{ style: { color: "dark.contrastText" } }}
                  InputLabelProps={{ style: { color: "#bbb" } }}
                  sx={{ "& .MuiOutlinedInput-root": { background: "dark.main" } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <MDInput
                  label="Επίθετο"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  fullWidth
                  inputProps={{ style: { color: "dark.contrastText" } }}
                  InputLabelProps={{ style: { color: "#bbb" } }}
                  sx={{ "& .MuiOutlinedInput-root": { background: "dark.main" } }}
                />
              </Grid>

              <Grid item xs={12}>
                <MDInput
                  label="Εταιρεία"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  fullWidth
                  inputProps={{ style: { color: "dark.contrastText" } }}
                  InputLabelProps={{ style: { color: "#bbb" } }}
                  sx={{ "& .MuiOutlinedInput-root": { background: "dark.main" } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <MDInput
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  fullWidth
                  inputProps={{ style: { color: "dark.contrastText" } }}
                  InputLabelProps={{ style: { color: "#bbb" } }}
                  sx={{ "& .MuiOutlinedInput-root": { background: "dark.main" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MDInput
                  label="Κινητό"
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  fullWidth
                  inputProps={{ style: { color: "dark.contrastText" } }}
                  InputLabelProps={{ style: { color: "#bbb" } }}
                  sx={{ "& .MuiOutlinedInput-root": { background: "dark.main" } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <MDInput
                  label="Σταθερό"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  fullWidth
                  inputProps={{ style: { color: "dark.contrastText" } }}
                  InputLabelProps={{ style: { color: "#bbb" } }}
                  sx={{ "& .MuiOutlinedInput-root": { background: "dark.main" } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl
                  sx={{
                    "& .MuiInputBase-root": { background: "dark.main", color: "dark.contrastText" },
                    "& .MuiInputLabel-root": { color: "#bbb" },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "dark.contrastText" },
                  }}
                  fullWidth
                >
                  <InputLabel id="status-label" sx={{ color: "#bbb" }}>
                    Κατάσταση
                  </InputLabel>
                  <Select
                    labelId="status-label"
                    label="Κατάσταση"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: "dark.main",
                          color: "dark.contrastText",
                        },
                      },
                    }}
                  >
                    <MenuItem value="active">Ενεργός</MenuItem>
                    <MenuItem value="inactive">Ανενεργός</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        <DialogActions
          sx={{
            bgcolor: "dark.main",
            color: "dark.contrastText",
            borderTop: "1px solid",
            borderColor: "dark.main",
          }}
        >
          <MDButton
            variant="text"
            color="secondary"
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            ΑΚΥΡΩΣΗ
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            form="client-form"
            type="submit"
            disabled={saving}
          >
            {saving ? "Αποθήκευση..." : "ΑΠΟΘΗΚΕΥΣΗ"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Client Confirmation */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: "dark.main",
            color: "dark.contrastText",
            borderBottom: "1px solid",
            borderColor: "dark.main",
          }}
        >
          Επιβεβαίωση διαγραφής
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            bgcolor: "dark.main",
            color: "dark.contrastText",
          }}
        >
          <MDTypography variant="button">
            Θέλεις σίγουρα να διαγράψεις αυτόν τον πελάτη;
          </MDTypography>
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: "dark.main",
            color: "dark.contrastText",
            borderTop: "1px solid",
            borderColor: "dark.main",
          }}
        >
          <MDButton
            variant="text"
            color="secondary"
            onClick={() => setDeleteDialog({ open: false, id: null })}
          >
            Άκυρο
          </MDButton>
          <MDButton variant="contained" color="error" onClick={handleConfirmDelete}>
            Διαγραφή
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Clients;
